/**
 * Where each stage wants her — the goal table `Wayfinder` reads (D-209).
 *
 * `src/story/` may not know where anything is (every beat file says so in its
 * header), so the goal of a stage lives here, on the world side, keyed by the
 * stage id the story already exposes. A stage with no entry has no goal and
 * gets no look: the field in beat 7, a held shot, a clock.
 *
 * **A goal is the place the stage is waiting for her to go**, not the thing
 * the stage is about. Where a stage waits on a verb, the goal is where the verb
 * is done, and `reach` stops the look as she arrives — from there her snout
 * and the verb prompt already say the rest.
 *
 * `wayfinding.spec.ts` holds every entry to two rules: its stage id exists in
 * the beat, and the stage's patience clock does not end it before the looks
 * (and calls) have had their turn — a clock is a backstop, not a cue.
 */

import type { WayGoal } from '@/systems/wayfinding';
import { OUT_FLAGS } from '@/story/act1/out';
import {
  BOARDS_AT, CULVERT_ROUTE, DITCH_AT, HEDGE_GAP_AT, HEDGEROW_SPAWN, HERD_HUDDLE_AT, PAST_FIELD_FENCE_Z, hedgerowGroundY,
} from '@/world/hedgerow';
import { HUDDLE_OBSERVE_REACH } from '@/game/beats/twelveOut';
import {
  BLACKOUT_FUSE_INTERACT_AT, BLACKOUT_OPEN_CRATES, BLACKOUT_PASS_PEN_Z, DECK_Y, FAT_HALL, OTTO_AT, OTTO_GAP_AT,
  STRAW_PEN, VENT_AT, VENT_REACH, blackoutCrateInteractAt, penBounds,
} from '@/world/fattening';
import { BLACKOUT_HALL_IDS } from '@/story/act2/blackoutHall';
import { OFFICE, OFFICE_EXIT } from '@/world/office';
import { WOOL_SHED_PALLET_ROOT } from '@/world/woolShed';
import { CORRIDOR } from '@/world/serviceCorridor';
import { BORN_DOOR_WATCH_AT, BORN_MOTHER_FLANK_AT, BORN_SIBLING_AT, BORN_STRAW_AT } from '@/world/bornBay';
import { CULL_BIN, CULL_BIN_AT } from '@/world/cullCorridor';
import { DOOR_APPROACH, WOOL_SHED_DOORWAY, WOOL_SHED_IDS } from '@/story/act2/woolShedPuzzle';
import { DAWN_TRANSPORT_FLAGS } from '@/story/act3/dawnTransport';
import {
  KESTNER_OBSERVE_AT, RACE_GATE_AT, UNDER_TRAILER_AT, VEHICLE_GATE, YARD_DAWN_JUNCTION_BOX_INTERACT, YARD_DAWN_UNDER_TRAILER,
  sideOfTruck,
} from '@/world/loadingYard';

export interface WayContext {
  readonly self: { readonly x: number; readonly y: number; readonly z: number };
  /** Whether a story flag is up, or an interaction with this id has completed. */
  has(flag: string): boolean;
}

/**
 * A goal, or null for none this tick. `variant` names which of a stage's goals
 * this is, so a stage that changes its mind restarts the stall clock.
 */
export type StageGoal = (ctx: WayContext) => (WayGoal & { variant?: string }) | null;

export interface WayGoalTable {
  readonly beat: number;
  readonly part: number;
  readonly goals: Readonly<Record<string, StageGoal>>;
}

// ── Beat 7 — Out ─────────────────────────────────────────────────────────────

/**
 * Where the roll that breaks the boards starts: back up the line she came
 * down, at the trailer's edge. `BOARDS_BREAK_SPEED` needs most of the eight
 * metres from the trailer, so the look has to point at the start of them, not
 * merely away from the fence.
 */
const BOARDS_RUN_UP = { x: BOARDS_AT.x, z: HEDGEROW_SPAWN.z - 1.5 } as const;

const OUT_GOALS: WayGoalTable = {
  beat: 7,
  part: 1,
  goals: {
    // **The fence, and then the run-up.** The two verbs on the timber fail by
    // design (`out.ts`), and failing honestly is a shudder and a creak — then,
    // if she stands at the boards, she looks back up the eight metres she will
    // have to roll. Standing at the run-up, she looks at the boards again.
    'the-boards': (ctx) => {
      const tried = ctx.has(OUT_FLAGS.boardsTried);
      const atRunUp = Math.hypot(ctx.self.x - BOARDS_RUN_UP.x, ctx.self.z - BOARDS_RUN_UP.z) < 1.5;
      if (tried && !atRunUp) {
        return {
          variant: 'run-up',
          x: BOARDS_RUN_UP.x,
          y: hedgerowGroundY(BOARDS_RUN_UP.x, BOARDS_RUN_UP.z) + 0.4,
          z: BOARDS_RUN_UP.z,
          reach: 1.5,
        };
      }
      return {
        variant: tried ? 'boards-again' : 'boards',
        x: BOARDS_AT.x,
        y: hedgerowGroundY(BOARDS_AT.x, BOARDS_AT.z) + 0.5,
        z: BOARDS_AT.z,
        // Tried once: the boards are no longer somewhere to arrive, they are
        // what she is looking at from the run-up, so there is no reach to stop
        // the look short of them.
        reach: tried ? 0 : 1.6,
      };
    },
    // The water, straight downhill. The run "cannot be reached by anybody
    // playing" (`out.ts`): this only speaks to somebody who stopped on the grass.
    'the-run': () => ({
      x: BOARDS_AT.x,
      y: DITCH_AT.bedY + 0.4,
      z: (DITCH_AT.channelNearZ + DITCH_AT.channelFarZ) / 2,
      reach: 1.5,
    }),
    // The one gap in the near hedge, nine metres west of where the run lands.
    'the-ditch': () => ({
      x: HEDGE_GAP_AT.x,
      y: hedgerowGroundY(HEDGE_GAP_AT.x, HEDGE_GAP_AT.z) + 0.5,
      z: HEDGE_GAP_AT.z,
      reach: 1.2,
    }),
  },
};

// ── Beat 3 — Otto ────────────────────────────────────────────────────────────

const STRAW = penBounds(STRAW_PEN);
/**
 * The far end of the alley, where it opens into the cross-passage the vent is
 * in. The vent is round the corner behind the last pen (`FAR_LAMP_NAME`'s
 * comment, D-087), so a look straight at it from the straw pen faces a pen
 * wall: she looks down the alley first, and at the vent once she is in the
 * passage and can see it.
 */
const ALLEY_END = { x: 0, z: FAT_HALL.halfLength - 1.2 } as const;

const OTTO_GOALS: WayGoalTable = {
  beat: 3,
  part: 1,
  goals: {
    // No clock on either (`otto.ts`: walking up an alley is not a puzzle), so
    // the look is the only thing that ever speaks to a player who does not.
    // `reach` is `atHisPen`'s own five metres.
    arrive: () => ({ x: OTTO_AT.x, y: 0.9, z: OTTO_AT.z, reach: 5 }),
    'find-him': () => ({ x: OTTO_AT.x, y: 0.9, z: OTTO_AT.z, reach: 5 }),
    // The broken corner — where Otto himself is looking.
    'in-with-him': () => ({ x: OTTO_GAP_AT.x, y: 0.25, z: OTTO_GAP_AT.z, reach: 0.6 }),
    // The straw: inside the pen is arrival, whatever she does there.
    hers: () => ({
      x: (STRAW.minX + STRAW.maxX) / 2,
      y: DECK_Y + 0.3,
      z: (STRAW.minZ + STRAW.maxZ) / 2,
      reach: Math.min(STRAW.maxX - STRAW.minX, STRAW.maxZ - STRAW.minZ) / 2,
    }),
    // Three legs, because the vent is two corners away from the straw: out of
    // the pen's mouth, down the alley, then the vent. Pointed straight at the
    // alley's end from inside the pen she faced its divider (frame
    // `b3-way-out-after`, session 71).
    'the-way-out': (ctx) => {
      if (ctx.self.x < STRAW.maxX && ctx.self.z > STRAW.minZ && ctx.self.z < STRAW.maxZ) {
        return { variant: 'mouth', x: STRAW.maxX + 0.5, y: 0.4, z: (STRAW.minZ + STRAW.maxZ) / 2, reach: 0.6 };
      }
      return ctx.self.z < ALLEY_END.z - 0.5
        ? { variant: 'alley', x: ALLEY_END.x, y: 0.6, z: ALLEY_END.z, reach: 0.8 }
        : { variant: 'vent', x: VENT_AT.x, y: 0.25, z: VENT_AT.z, reach: VENT_REACH };
    },
  },
};

// ── Beat 8 part 2 — Blackout, the hall ──────────────────────────────────────

const BLACKOUT_GOALS: WayGoalTable = {
  beat: 8,
  part: 2,
  goals: {
    // Past Otto's empty pen: the plane the stage waits on, in the alley.
    'pass-pen': () => ({ x: 0, y: 0.6, z: BLACKOUT_PASS_PEN_Z + 0.6, reach: 0.6 }),
    'fuse': () => ({ ...BLACKOUT_FUSE_INTERACT_AT, reach: 0.9 }),
    // Each crate in turn; with both open there is nothing left to point at —
    // the stage is the flock running itself out.
    crates: (ctx) => {
      const ids = [BLACKOUT_HALL_IDS.crateA, BLACKOUT_HALL_IDS.crateB];
      const next = BLACKOUT_OPEN_CRATES.findIndex((_crate, index) => {
        const id = ids[index];
        return id !== undefined && !ctx.has(id);
      });
      if (next < 0) return null;
      return { variant: `crate-${next}`, ...blackoutCrateInteractAt(next), reach: 0.9 };
    },
    // `torch` has no goal: he walks the alley and she keeps out of his light,
    // which is a place to NOT be.
  },
};

// ── Beat 1 — Born, the three verbs ──────────────────────────────────────────

const BORN_GOALS: WayGoalTable = {
  beat: 1,
  part: 1,
  goals: {
    // Spec §7's tutorial by curiosity: each verb is offered on its own stage
    // with a 45 s backstop, and until D-209 nothing turned her toward any of
    // them, so three missed verbs were up to 135 s of a piglet lying still
    // (audit row 20). The goal of each is the verb's own interactable point.
    nudge: () => ({ ...BORN_MOTHER_FLANK_AT, reach: 0.75 }),
    // D-250: her sibling asleep under the lamp. The stage ends on the nudge
    // that wakes it, so for the whole stage it is where it sleeps.
    sibling: () => ({ ...BORN_SIBLING_AT, reach: 0.7 }),
    root: () => ({ ...BORN_STRAW_AT, reach: 0.6 }),
    // `observe` works from across the pen and needs her snout on the doorway,
    // not her body at it: no reach, so a stall anywhere turns her to it.
    look: () => ({ ...BORN_DOOR_WATCH_AT, reach: 0 }),
  },
};

// ── Beat 4 — The Service Corridor, the door ────────────────────────────────

/**
 * Where the plan wants her when the door opens: in the short leg's pipe lane,
 * at its door end, beside the frame (`docs/beat-4-plan.md`, "the pressure
 * plate"). The lane is the only cover in that leg (`serviceCover()`), so the
 * look is at where waiting is survivable, not at the door itself — a player
 * already looking at the door does not need turning to it.
 */
const SERVICE_DOOR_WAIT = {
  x: CORRIDOR.pipeShortLegMaxX - 0.3,
  z: (CORRIDOR.shortMinZ + CORRIDOR.pipeOuterFaceZ) / 2 + 0.2,
} as const;

const SERVICE_GOALS: WayGoalTable = {
  beat: 4,
  part: 1,
  goals: {
    // The mat is built for boots and the plan forbids a prompt, a highlight or
    // a hint at it. What it allows is D-209's first layer: stalled — most
    // often standing on the mat, which only knocks the latch — she turns to
    // the lane by the frame. `reach` stops short of the mat, so
    // standing on it (1.27 m off) still counts as not there (audit row 12).
    'the-door': () => ({ x: SERVICE_DOOR_WAIT.x, y: 0.3, z: SERVICE_DOOR_WAIT.z, reach: 0.9 }),
  },
};

// ── Beat 9 — Wool and Wire, the puzzle ──────────────────────────────────────

const WOOL_SHED_GOALS: WayGoalTable = {
  beat: 9,
  part: 1,
  goals: {
    // One stage, no clock (`woolShedPuzzle.ts`: a clock would end the beat under
    // a player solving it), so until D-209 nothing ever spoke to a player at
    // the shut north door, and the pallet's `root` is off to the side of it
    // (audit row 7). First the pallet, at its own interaction reach; once it is
    // over and Holly is up, the door — `reach` covers `DOOR_APPROACH`, where
    // she stops being the player's job and becomes Holly's.
    puzzle: (ctx) => {
      if (!ctx.has(WOOL_SHED_IDS.pallet)) {
        return { variant: 'pallet', x: WOOL_SHED_PALLET_ROOT.x, y: 0.3, z: WOOL_SHED_PALLET_ROOT.z, reach: 0.85 };
      }
      return {
        variant: 'door',
        x: 0,
        y: 0.9,
        z: WOOL_SHED_DOORWAY.min.z,
        reach: WOOL_SHED_DOORWAY.min.z - DOOR_APPROACH.min.z,
      };
    },
  },
};

// ── Beat 10 — Twelve Out ────────────────────────────────────────────────────

/** Where the fall runs out: the route's turn at the top of the bank, above the channel. */
const BANK_TOP = CULVERT_ROUTE[1];
/** The mouth of the pipe, the route's end. */
const PIPE_MOUTH = CULVERT_ROUTE[3];

/**
 * The way to the pipe from anywhere in the field, in legs, because the pipe is
 * twenty metres west and twenty-five south of the spawn and out of every
 * opening frame (audit row 10):
 *
 *  1. **north of the fence, the bay** — every stage after the first can be
 *     reached there by its clock, and from the farm side a look at anything
 *     downfield faces the fence;
 *  2. **the top of the bank** — the herd's own route (`CULVERT_ROUTE`), so
 *     the look leads the string past the dog's post rather than off the
 *     corridor the queue measures;
 *  3. **the mouth**, once she is past the bank's top or down in the ditch —
 *     along the channel, which is cover (D-167), or from the top of the bank
 *     a look down into the water at the headwall.
 *
 * Not split on `culvertRoutePlace`: from the channel east of the pipe the
 * nearest route segment is the fall, and the projection would point her back
 * up the bank away from a pipe she can see. Not on z alone either: from the
 * east of the field at the bank's height a look at the mouth runs over the
 * dog's post (Codex review, session 74).
 */
function wayToPipe(ctx: WayContext): WayGoal & { variant: string } {
  if (ctx.self.z > PAST_FIELD_FENCE_Z) {
    return { variant: 'bay', x: BOARDS_AT.x, y: hedgerowGroundY(BOARDS_AT.x, BOARDS_AT.z) + 0.5, z: BOARDS_AT.z, reach: 0.3 };
  }
  const inTheDitch = ctx.self.z <= DITCH_AT.nearBankZ;
  const pastTheBankTop = ctx.self.x <= BANK_TOP.x + 0.8 && ctx.self.z <= BANK_TOP.z + 0.8;
  if (!inTheDitch && !pastTheBankTop) {
    return { variant: 'bank', x: BANK_TOP.x, y: hedgerowGroundY(BANK_TOP.x, BANK_TOP.z) + 0.4, z: BANK_TOP.z, reach: 0.8 };
  }
  return { variant: 'pipe', x: PIPE_MOUTH.x, y: DITCH_AT.bedY + 0.5, z: PIPE_MOUTH.z, reach: 0.5 };
}

const TWELVE_OUT_GOALS: WayGoalTable = {
  beat: 10,
  part: 1,
  goals: {
    // She spawns facing the bay, six metres off, so this look only ever
    // speaks to somebody who turned away from it and stopped.
    'the-field': (ctx) => wayToPipe(ctx),
    // The twelve, until she is in reach of the count; from the farm side, the
    // bay first. After the count the huddle is still observable, but the
    // stage has moved on and so does the look.
    'the-count': (ctx) => {
      if (ctx.self.z > PAST_FIELD_FENCE_Z) return wayToPipe(ctx);
      return {
        variant: 'huddle',
        x: HERD_HUDDLE_AT.x,
        y: hedgerowGroundY(HERD_HUDDLE_AT.x, HERD_HUDDLE_AT.z) + 0.4,
        z: HERD_HUDDLE_AT.z,
        reach: HUDDLE_OBSERVE_REACH,
      };
    },
    // The stage waits on the string leaving the huddle, and it leaves when she
    // leads it down its route — so the goal is the route, not the dog. What
    // the dog costs is said by the herd's stall voice and by `F`'s prompt;
    // a look at the dog would point her at the one thing she must not walk to.
    'the-dog': (ctx) => wayToPipe(ctx),
    'the-run': (ctx) => wayToPipe(ctx),
  },
};

// ── Beat 11 — The Tally ─────────────────────────────────────────────────────

const OFFICE_GOALS: WayGoalTable = {
  beat: 11,
  part: 1,
  goals: {
    // The door he did not shut: the middle of the exit predicate's clear chord,
    // just inside the wall, which is where `beat-11-office.spec.ts` aims too.
    crossing: () => ({
      x: (OFFICE_EXIT.minX + OFFICE_EXIT.maxX) / 2,
      y: 0.5,
      z: OFFICE.minZ + 0.4,
      reach: 0.5,
    }),
  },
};

// ── Beat 5 — The Cull Corridor, the bin ─────────────────────────────────────

/** Where the toppled bin's mouth is, facing the door (`CULL_BIN`). */
const CULL_BIN_MOUTH_Z = CULL_BIN_AT.z + CULL_BIN.tipPivotDz + CULL_BIN.tipLength;
/** Past the mouth on the open side of the corridor: the way round to it. */
const CULL_BIN_ROUND = { x: CULL_BIN_AT.x + CULL_BIN.halfX + 1.0, z: CULL_BIN_MOUTH_Z + 0.35 } as const;

const CULL_GOALS: WayGoalTable = {
  beat: 5,
  part: 1,
  goals: {
    // The red lamp leads her to the door, which is the wrong thing and is
    // meant to be: `the-door` is six seconds of finding out it is not a puzzle,
    // and has no goal. From `the-bin` on, the way out is the bin beside it
    // (audit row 13): first the bin at `nudge` reach, then — it lies with its
    // mouth to the door — round its open side, then in.
    'the-bin': () => ({ x: CULL_BIN_AT.x, y: 0.5, z: CULL_BIN_AT.z, reach: 1.0 }),
    //
    // Three legs, not two: past the mouth's line but still off to the side,
    // the interior is behind the bin's corner, so she is pointed at the front
    // of the opening first and only in front of it at the inside. Switching on
    // z alone cut the look across the shell (independent review, session 76).
    'get-in': (ctx) => {
      if (ctx.self.z < CULL_BIN_MOUTH_Z) {
        return { variant: 'round', x: CULL_BIN_ROUND.x, y: 0.3, z: CULL_BIN_ROUND.z, reach: 0.6 };
      }
      if (Math.abs(ctx.self.x - CULL_BIN_AT.x) > CULL_BIN.halfX) {
        return { variant: 'front', x: CULL_BIN_AT.x, y: 0.3, z: CULL_BIN_MOUTH_Z + 0.45, reach: 0.25 };
      }
      return { variant: 'mouth', x: CULL_BIN_AT.x, y: 0.3, z: CULL_BIN_MOUTH_Z - 0.4, reach: 0.3 };
    },
  },
};

// ── Beat 6 — The Loading Yard ───────────────────────────────────────────────

/**
 * The gap under the trailer, low: the deck is at 1.09 m, so a target at 0.4 m
 * puts her snout and the follow camera's centre on the shadow under it, not on
 * the box above it. `reach` is inside `isUnderTrailer`'s own rectangle, so the
 * look stops only once the stage is about to end on it.
 */
const UNDER_TRAILER: WayGoal = { x: UNDER_TRAILER_AT.x, y: 0.4, z: UNDER_TRAILER_AT.z, reach: 0.8 };

const LOADING_YARD_GOALS: WayGoalTable = {
  beat: 6,
  part: 1,
  goals: {
    // Out of the bins, and the first look at the yard is at where the beat
    // ends (audit row 9: nothing said the trailer was the endpoint). Any step
    // into the yard ends the stage, so this is only ever seen by somebody who
    // climbed out and stood still.
    'out-of-the-bin': () => UNDER_TRAILER,
    // Him, and no `reach`: `observe` is aimed, not walked to — its reach is
    // 15 m, its cone ±1.2 rad, and from the bins he is 21 m away and 72° off
    // her nose. The look is the facing the verb needs; W from there is the range.
    'the-count': () => ({ x: KESTNER_OBSERVE_AT.x, y: KESTNER_OBSERVE_AT.y, z: KESTNER_OBSERVE_AT.z, reach: 0 }),
    // `the-first-flash` has no goal: its clock is 8 s, shorter than one look.
    'the-crossing': () => UNDER_TRAILER,
  },
};

// ── Beat 12 — Dawn Transport ────────────────────────────────────────────────

/**
 * Where she calls Holly from: beside the race gate, on the yard side — where
 * `cold-spawn.spec.ts` presses F, well inside `CALL_RANGE`.
 */
const HOLLY_CALL_AT = { x: RACE_GATE_AT.x + 1.2, z: RACE_GATE_AT.z + 1.2 } as const;

const DAWN_TRANSPORT_GOALS: WayGoalTable = {
  beat: 12,
  part: 1,
  goals: {
    // Marks 1 and 2 in either order, and the look follows what is left: the
    // box while the floods are on, whatever else is done; with them out, Holly.
    // Nothing pointed at the TRAILER, and the trailer is the route — the way
    // round the cab meets the driver head-on as mark 1 sends him to the box
    // (audit row 24). So from the shed side the look goes to the gap under
    // it first, and only from the race side to the gate.
    'the-plan': (ctx) => {
      if (!ctx.has(DAWN_TRANSPORT_FLAGS.lightsOut)) {
        return { variant: 'box', ...YARD_DAWN_JUNCTION_BOX_INTERACT, reach: 1.15 };
      }
      if (ctx.has(DAWN_TRANSPORT_FLAGS.gateHeld)) return null;
      // Past the axis by her own length, so she does not flip back and forth under it.
      if (sideOfTruck(ctx.self.x, ctx.self.z) > -0.5) {
        return { variant: 'trailer', x: YARD_DAWN_UNDER_TRAILER.x, y: 0.3, z: YARD_DAWN_UNDER_TRAILER.z, reach: 0.8 };
      }
      return { variant: 'holly', x: HOLLY_CALL_AT.x, y: 0.6, z: HOLLY_CALL_AT.z, reach: 1.2 };
    },
    // Mark 3: the herd follows her, so the goal is where she leads it.
    'the-herd': () => ({ x: VEHICLE_GATE.centreX, y: 0.8, z: VEHICLE_GATE.z, reach: 1.5 }),
  },
};

export const WAY_GOAL_TABLES: readonly WayGoalTable[] = [
  BORN_GOALS, OTTO_GOALS, SERVICE_GOALS, CULL_GOALS, LOADING_YARD_GOALS, OUT_GOALS, BLACKOUT_GOALS, WOOL_SHED_GOALS, TWELVE_OUT_GOALS, OFFICE_GOALS, DAWN_TRANSPORT_GOALS,
];

/**
 * The goal for this tick, with the key `Wayfinder.setGoal` compares — beat,
 * part, stage and variant — or null.
 */
export function wayGoalFor(
  beat: number,
  part: number,
  stageId: string | null,
  ctx: WayContext,
): { key: string; goal: WayGoal } | null {
  if (stageId === null) return null;
  const table = WAY_GOAL_TABLES.find((t) => t.beat === beat && t.part === part);
  const stageGoal = table?.goals[stageId];
  if (stageGoal === undefined) return null;
  const goal = stageGoal(ctx);
  if (goal === null) return null;
  return { key: `${beat}.${part}:${stageId}:${goal.variant ?? ''}`, goal };
}
